// Bouton d'enregistrement vocal réutilisable.
// 2 modes : clic (démarre), re-clic (arrête) — ou clic prolongé maintenu, relâchement arrête.
import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function VoiceRecorderButton({ onText, soinId, transcribe, label = "Enregistrer un vocal", className = "sx-info", testId = "voice-record-btn", ...rest }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingRef = useRef(false);
  const holdStartRef = useRef(0);

  const doTranscribe = async (blob) => {
    if (transcribe) return transcribe(blob);
    const fd = new FormData(); fd.append("file", blob, "note.webm");
    const { data } = await api.post(`/soins/${soinId}/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.text;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecording(false); recordingRef.current = false; setBusy(true);
        try {
          const text = await doTranscribe(blob);
          if (text) { onText(text); toast.success("Vocal transcrit"); } else toast.info("Aucune parole détectée.");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Transcription impossible"); }
        finally { setBusy(false); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true); recordingRef.current = true;
    } catch (e) { toast.error("Micro inaccessible — autorisez l'accès au microphone."); }
  };
  const stop = () => mediaRef.current?.stop();

  const onPointerDown = () => {
    if (busy) return;
    if (recordingRef.current) { stop(); return; }
    holdStartRef.current = Date.now();
    const onUp = () => {
      window.removeEventListener("pointerup", onUp);
      const held = Date.now() - holdStartRef.current;
      if (held >= 400 && recordingRef.current) stop(); // clic prolongé : relâcher arrête
      // clic court : on laisse tourner (mode bascule), le prochain clic arrête
    };
    window.addEventListener("pointerup", onUp);
    start();
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onPointerDown={onPointerDown}
      disabled={busy}
      className={className}
      title="Clic pour démarrer/arrêter, ou maintenez pour enregistrer"
      style={{ background: recording ? "oklch(0.55 0.18 25)" : "var(--bg-warm)", color: recording ? "#fff" : "var(--text-2)", opacity: busy ? 0.6 : 1, touchAction: "none" }}
      {...rest}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : recording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
      {busy ? "Transcription…" : recording ? "Arrêter / relâcher" : label}
    </button>
  );
}
